import { StoreOrder } from '../../models';
import { Modal } from '../ui/Modal';
import { safeImageUrl } from '../../utils/url';
import { formatCurrency, formatInstantDate } from '../../utils/orderFormat';
import { OrderStatusBadge } from './OrderStatusBadge';
import { OrderPaymentList } from './OrderPaymentList';
import { OrderReservationNotice } from './OrderReservationNotice';
import { getOrderStatusView } from './orderStatus';

interface OrderDetailsModalProps {
  order: StoreOrder | null;
  onClose: () => void;
}

export function OrderDetailsModal({ order, onClose }: OrderDetailsModalProps) {
  if (!order) return null;

  const { nextStep } = getOrderStatusView(order, false);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Pedido #${order.id}`}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="w-full min-h-10 px-4 text-sm font-semibold text-white bg-chocolate-dark hover:bg-chocolate-deep rounded-full transition-colors"
        >
          Fechar
        </button>
      }
    >
      <div className="space-y-6">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <OrderStatusBadge order={order} />
            {nextStep && <p className="text-sm text-text-medium">{nextStep}</p>}
          </div>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-text-medium">Data do pedido</dt>
              <dd className="text-sm font-medium text-text-dark">{formatInstantDate(order.saleDate)}</dd>
            </div>
            <div>
              <dt className="text-sm text-text-medium">Total</dt>
              <dd className="text-base font-bold text-text-dark tabular-nums">{formatCurrency(order.totalValue)}</dd>
            </div>
          </dl>
          <div className="-mx-5">
            <OrderReservationNotice order={order} />
          </div>
        </div>

        <section>
          <h3 className="text-sm font-semibold text-text-dark mb-2">Itens</h3>
          <ul className="space-y-2">
            {order.items.map((item, idx) => (
              <li key={`${item.id}-${idx}`} className="flex items-center gap-3 bg-champagne-light rounded-xl px-3 py-2.5">
                {item.imageUrl ? (
                  <img src={safeImageUrl(item.imageUrl)} alt="" className="w-11 h-11 rounded-lg object-cover bg-champagne shrink-0" />
                ) : (
                  <span className="w-11 h-11 rounded-lg bg-champagne shrink-0" aria-hidden="true" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-dark truncate">{item.productName}</p>
                  <p className="text-xs text-text-medium">
                    {item.variantLabel ? `${item.variantLabel} · ` : ''}
                    {item.quantity}x {formatCurrency(item.unitPrice)}
                  </p>
                </div>
                <span className="text-sm font-semibold text-text-dark tabular-nums shrink-0">{formatCurrency(item.totalPrice)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-text-dark mb-2">Pagamento</h3>
          <OrderPaymentList payments={order.paymentMethods} />
        </section>

        {order.observation && (
          <section>
            <h3 className="text-sm font-semibold text-text-dark mb-2">Observação</h3>
            <p className="text-sm text-text-dark bg-champagne-light rounded-xl px-3 py-2.5">{order.observation}</p>
          </section>
        )}
      </div>
    </Modal>
  );
}
